import { useLoaderData, useParams, type LoaderFunctionArgs } from 'react-router';
import { FincurryLogo } from '@/components/brand/fincurry-logo';
import { callServer } from '../api/server';
import { seg } from '../api/respond';
import { inviteOneLinkUrl } from '@/lib/public-config';

interface Invite {
  groupName: string;
  isRevoked: boolean;
}

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.tailwebs.fincurryapp';
const APP_STORE_URL = 'https://apps.apple.com/app/fincurry/id6779744929';

// The invite summary, from the server's public endpoint. An unknown token
// or any failure shows the not-found page rather than a half-working
// invite. The visitor is not signed in to anything, and need not be.
export async function loader({ params }: LoaderFunctionArgs): Promise<Invite> {
  const result = await callServer<Invite>('GET', `/api/v1/public/invites/${seg(params.token!)}`, {
    expectSignedIn: false,
  });
  if (!result.ok) throw new Response('Not Found', { status: 404, statusText: 'Not Found' });
  return result.data;
}

export default function InviteLandingPage() {
  const { groupName, isRevoked } = useLoaderData() as Invite;
  const token = useParams().token ?? '';
  // Links don't expire; only an admin revoking one turns it off.
  const expired = isRevoked;

  const playStoreUrl = PLAY_STORE_URL;
  const appStoreUrl = APP_STORE_URL;
  // AppsFlyer OneLink with the token (deferred deep link after install).
  const oneLinkUrl = inviteOneLinkUrl(token, groupName);

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-[-30%] left-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-500/[0.03] blur-[120px]" />
      <div className="absolute bottom-[-20%] right-[-5%] w-[400px] h-[400px] rounded-full bg-emerald-500/[0.02] blur-[100px]" />

      <div className="relative w-full max-w-[400px] text-center">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <FincurryLogo size="lg" />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2">You&apos;re Invited!</h1>

        {expired ? (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">This invite link has been turned off.</p>
            <p className="text-zinc-600 text-xs">Ask the group admin for a new invite.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-zinc-400 text-sm">
              You&apos;ve been invited to join <span className="text-white font-semibold">{groupName}</span> on Fincurry
            </p>

            {/* App info */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5 text-left space-y-3">
              <p className="text-[13px] text-zinc-300 font-medium">Fincurry helps families:</p>
              <ul className="space-y-2">
                {['Track expenses together', 'Set shared budgets', 'Split bills easily', 'Get spending insights'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-[13px] text-zinc-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Download buttons */}
            <div className="space-y-3">
              <a
                href={oneLinkUrl}
                className="block w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-black font-semibold text-sm text-center shadow-lg shadow-emerald-500/20 hover:from-emerald-400 hover:to-emerald-500 transition-all"
              >
                Open in Fincurry
              </a>

              <div className="flex gap-3">
                <a
                  href={playStoreUrl}
                  className="flex-1 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-zinc-300 text-[12px] font-medium text-center hover:bg-white/[0.06] transition-all"
                >
                  Google Play
                </a>
                <a
                  href={appStoreUrl}
                  className="flex-1 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-zinc-300 text-[12px] font-medium text-center hover:bg-white/[0.06] transition-all"
                >
                  App Store
                </a>
              </div>
            </div>

            {/* Invite code fallback */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold mb-2">Invite Code</p>
              <p className="text-[13px] text-zinc-300 font-mono break-all select-all">{token}</p>
              <p className="text-[10px] text-zinc-600 mt-2">Open app → Profile → My Groups → Join with code</p>
            </div>
          </div>
        )}

        <p className="text-zinc-700 text-[11px] mt-8">
          Fincurry © {new Date().getFullYear()} · Tailwebs
        </p>
      </div>
    </div>
  );
}
